import Phaser from 'phaser';
import { GameConfig } from '../config/GameConfig';
import { Player } from './Player';

export type EnemyType = 'normal' | 'fast' | 'tank' | 'elite' | 'splitter' | 'ranged' | 'dasher';

export class Enemy extends Phaser.Physics.Arcade.Sprite {
  public health: number;
  public maxHealth: number;
  private target: Player;
  private enemyType: EnemyType;
  private speed: number;
  private dashTimer?: Phaser.Time.TimerEvent;
  private dashCooldown: number = 3000;
  private isDashing: boolean = false;
  private shootTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene, x: number, y: number, target: Player, type: EnemyType = 'normal') {
    super(scene, x, y, '');

    this.enemyType = type;
    this.target = target;

    // Set stats based on type
    switch (type) {
      case 'fast':
        this.health = 10;
        this.maxHealth = 10;
        this.speed = 150;
        break;
      case 'tank':
        this.health = 50;
        this.maxHealth = 50;
        this.speed = 60;
        break;
      case 'elite':
        this.health = 80;
        this.maxHealth = 80;
        this.speed = 70;
        break;
      case 'splitter':
        this.health = 25;
        this.maxHealth = 25;
        this.speed = 100;
        break;
      case 'ranged':
        this.health = 15;
        this.maxHealth = 15;
        this.speed = 70;
        break;
      case 'dasher':
        this.health = 18;
        this.maxHealth = 18;
        this.speed = 80;
        break;
      default: // normal
        this.health = 20;
        this.maxHealth = 20;
        this.speed = 90;
    }

    // Add to scene
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Create sprite based on type
    this.createEnemyTexture(scene);
    this.setTexture(`enemy-${type}`);

    // Setup physics
    this.setCollideWorldBounds(false);
  }

  private createEnemyTexture(scene: Phaser.Scene): void {
    switch (this.enemyType) {
      case 'fast':
        this.createFastMouseTexture(scene);
        break;
      case 'tank':
        this.createTankRatTexture(scene);
        break;
      case 'elite':
        this.createEliteRatTexture(scene);
        break;
      case 'splitter':
        this.createSplitterTexture(scene);
        break;
      case 'ranged':
        this.createRangedTexture(scene);
        break;
      case 'dasher':
        this.createDasherTexture(scene);
        break;
      default:
        this.createNormalMouseTexture(scene);
    }
  }

  private createNormalMouseTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = GameConfig.ENEMY.SIZE;
    const halfSize = size / 2;

    // Shadow
    graphics.fillStyle(0x000000, 0.15);
    graphics.fillEllipse(halfSize, halfSize + 8, 10, 3);

    // Body (gray oval with shading)
    graphics.fillStyle(0x909090, 1);
    graphics.fillEllipse(halfSize, halfSize + 1, 9, 11);
    graphics.fillStyle(0xA8A8A8, 1);
    graphics.fillEllipse(halfSize - 1, halfSize, 6, 8);

    // Head
    graphics.fillStyle(0x909090, 1);
    graphics.fillCircle(halfSize, halfSize - 3, 6.5);
    graphics.fillStyle(0xA8A8A8, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 4, 4);

    // Ears
    graphics.fillStyle(0x707070, 1);
    graphics.fillCircle(halfSize - 5, halfSize - 7, 3.5);
    graphics.fillCircle(halfSize + 5, halfSize - 7, 3.5);
    graphics.fillStyle(0xFFB6C1, 1);
    graphics.fillCircle(halfSize - 5, halfSize - 6.5, 2);
    graphics.fillCircle(halfSize + 5, halfSize - 6.5, 2);

    // Eyes (red glow)
    graphics.fillStyle(0xFF0000, 1);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 1);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 1);
    graphics.fillStyle(0xFF4444, 0.5);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 2);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 2);

    // Tail
    graphics.fillStyle(0x696969, 1);
    graphics.fillEllipse(halfSize + 5, halfSize + 4, 2, 6);

    graphics.generateTexture('enemy-normal', size, size);
    graphics.destroy();
  }

  private createFastMouseTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = GameConfig.ENEMY.SIZE;
    const halfSize = size / 2;

    // Shadow (smaller, elongated for speed)
    graphics.fillStyle(0x000000, 0.15);
    graphics.fillEllipse(halfSize, halfSize + 8, 12, 2);

    // Body (brown, sleeker)
    graphics.fillStyle(0x8B4513, 1);
    graphics.fillEllipse(halfSize, halfSize + 1, 8, 10);
    graphics.fillStyle(0xA0522D, 1);
    graphics.fillEllipse(halfSize - 1, halfSize, 5, 7);

    // Head (smaller)
    graphics.fillStyle(0x8B4513, 1);
    graphics.fillCircle(halfSize, halfSize - 3, 5.5);
    graphics.fillStyle(0xA0522D, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 4, 3);

    // Smaller ears
    graphics.fillStyle(0x654321, 1);
    graphics.fillCircle(halfSize - 4, halfSize - 6, 2.5);
    graphics.fillCircle(halfSize + 4, halfSize - 6, 2.5);

    // Eyes (yellow glow for fast)
    graphics.fillStyle(0xFFFF00, 1);
    graphics.fillCircle(halfSize - 2, halfSize - 4, 1);
    graphics.fillCircle(halfSize + 2, halfSize - 4, 1);
    graphics.fillStyle(0xFFFF88, 0.5);
    graphics.fillCircle(halfSize - 2, halfSize - 4, 2);
    graphics.fillCircle(halfSize + 2, halfSize - 4, 2);

    // Speed lines
    graphics.lineStyle(1, 0x8B4513, 0.4);
    graphics.lineBetween(halfSize - 8, halfSize, halfSize - 5, halfSize);
    graphics.lineBetween(halfSize - 8, halfSize + 2, halfSize - 5, halfSize + 2);

    graphics.generateTexture('enemy-fast', size, size);
    graphics.destroy();
  }

  private createTankRatTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = 32; // Larger
    const halfSize = size / 2;

    // Shadow (larger)
    graphics.fillStyle(0x000000, 0.2);
    graphics.fillEllipse(halfSize, halfSize + 12, 16, 5);

    // Body (dark gray, bulky)
    graphics.fillStyle(0x505050, 1);
    graphics.fillEllipse(halfSize, halfSize + 2, 13, 15);
    graphics.fillStyle(0x606060, 1);
    graphics.fillEllipse(halfSize - 1, halfSize + 1, 10, 12);

    // Head (larger)
    graphics.fillStyle(0x505050, 1);
    graphics.fillCircle(halfSize, halfSize - 4, 8);
    graphics.fillStyle(0x606060, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 5, 5);

    // Large ears
    graphics.fillStyle(0x404040, 1);
    graphics.fillCircle(halfSize - 6, halfSize - 9, 4);
    graphics.fillCircle(halfSize + 6, halfSize - 9, 4);
    graphics.fillStyle(0xFFB6C1, 1);
    graphics.fillCircle(halfSize - 6, halfSize - 8, 2.5);
    graphics.fillCircle(halfSize + 6, halfSize - 8, 2.5);

    // Eyes (dark red, menacing)
    graphics.fillStyle(0x8B0000, 1);
    graphics.fillCircle(halfSize - 3, halfSize - 5, 1.5);
    graphics.fillCircle(halfSize + 3, halfSize - 5, 1.5);
    graphics.fillStyle(0xFF0000, 0.6);
    graphics.fillCircle(halfSize - 3, halfSize - 5, 2.5);
    graphics.fillCircle(halfSize + 3, halfSize - 5, 2.5);

    // Armor plating
    graphics.lineStyle(2, 0x303030, 0.8);
    graphics.strokeCircle(halfSize, halfSize + 1, 11);

    graphics.generateTexture('enemy-tank', size, size);
    graphics.destroy();
  }

  private createEliteRatTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = 28; // Medium-large
    const halfSize = size / 2;

    // Shadow
    graphics.fillStyle(0x000000, 0.18);
    graphics.fillEllipse(halfSize, halfSize + 10, 14, 4);

    // Body (black with purple tint)
    graphics.fillStyle(0x2F1B3C, 1);
    graphics.fillEllipse(halfSize, halfSize + 1, 11, 13);
    graphics.fillStyle(0x4B2D5F, 1);
    graphics.fillEllipse(halfSize - 1, halfSize, 8, 10);

    // Head
    graphics.fillStyle(0x2F1B3C, 1);
    graphics.fillCircle(halfSize, halfSize - 3, 7);
    graphics.fillStyle(0x4B2D5F, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 4, 4.5);

    // Ears with purple glow
    graphics.fillStyle(0x1F0B2C, 1);
    graphics.fillCircle(halfSize - 5, halfSize - 8, 3.5);
    graphics.fillCircle(halfSize + 5, halfSize - 8, 3.5);
    graphics.fillStyle(0x9370DB, 0.4);
    graphics.fillCircle(halfSize - 5, halfSize - 8, 2);
    graphics.fillCircle(halfSize + 5, halfSize - 8, 2);

    // Eyes (purple glow)
    graphics.fillStyle(0x9370DB, 1);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 1.2);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 1.2);
    graphics.fillStyle(0xBA55D3, 0.6);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 2.5);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 2.5);

    // Crown/spikes
    graphics.fillStyle(0x9370DB, 0.8);
    graphics.fillTriangle(
      halfSize - 3, halfSize - 9,
      halfSize - 2, halfSize - 12,
      halfSize - 1, halfSize - 9
    );
    graphics.fillTriangle(
      halfSize, halfSize - 9,
      halfSize + 1, halfSize - 13,
      halfSize + 2, halfSize - 9
    );
    graphics.fillTriangle(
      halfSize + 3, halfSize - 9,
      halfSize + 4, halfSize - 12,
      halfSize + 5, halfSize - 9
    );

    graphics.generateTexture('enemy-elite', size, size);
    graphics.destroy();
  }

  private createSplitterTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = GameConfig.ENEMY.SIZE;
    const halfSize = size / 2;

    // Shadow
    graphics.fillStyle(0x000000, 0.15);
    graphics.fillEllipse(halfSize, halfSize + 8, 10, 3);

    // Body (green, slimy)
    graphics.fillStyle(0x228B22, 1);
    graphics.fillEllipse(halfSize, halfSize + 1, 9, 11);
    graphics.fillStyle(0x32CD32, 1);
    graphics.fillEllipse(halfSize - 1, halfSize, 6, 8);

    // Head
    graphics.fillStyle(0x228B22, 1);
    graphics.fillCircle(halfSize, halfSize - 3, 6.5);
    graphics.fillStyle(0x32CD32, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 4, 4);

    // Ears
    graphics.fillStyle(0x1B6B1B, 1);
    graphics.fillCircle(halfSize - 5, halfSize - 7, 3.5);
    graphics.fillCircle(halfSize + 5, halfSize - 7, 3.5);

    // Eyes (green glow)
    graphics.fillStyle(0x00FF00, 1);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 1);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 1);
    graphics.fillStyle(0x7FFF00, 0.5);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 2);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 2);

    // Split mark (crack down middle)
    graphics.lineStyle(1, 0x1B6B1B, 0.8);
    graphics.lineBetween(halfSize, halfSize - 6, halfSize, halfSize + 6);

    graphics.generateTexture('enemy-splitter', size, size);
    graphics.destroy();
  }

  private createRangedTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = GameConfig.ENEMY.SIZE;
    const halfSize = size / 2;

    // Shadow
    graphics.fillStyle(0x000000, 0.15);
    graphics.fillEllipse(halfSize, halfSize + 8, 10, 3);

    // Body (orange)
    graphics.fillStyle(0xFF8C00, 1);
    graphics.fillEllipse(halfSize, halfSize + 1, 9, 11);
    graphics.fillStyle(0xFFA500, 1);
    graphics.fillEllipse(halfSize - 1, halfSize, 6, 8);

    // Head
    graphics.fillStyle(0xFF8C00, 1);
    graphics.fillCircle(halfSize, halfSize - 3, 6.5);
    graphics.fillStyle(0xFFA500, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 4, 4);

    // Ears
    graphics.fillStyle(0xD2691E, 1);
    graphics.fillCircle(halfSize - 5, halfSize - 7, 3.5);
    graphics.fillCircle(halfSize + 5, halfSize - 7, 3.5);

    // Eyes (orange glow)
    graphics.fillStyle(0xFF4500, 1);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 1);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 1);
    graphics.fillStyle(0xFF6347, 0.5);
    graphics.fillCircle(halfSize - 2.5, halfSize - 4, 2);
    graphics.fillCircle(halfSize + 2.5, halfSize - 4, 2);

    // Quills/spines on back (for shooting)
    graphics.fillStyle(0x8B4513, 1);
    for (let i = 0; i < 3; i++) {
      graphics.fillTriangle(
        halfSize + 4 + i * 2, halfSize + 2,
        halfSize + 5 + i * 2, halfSize - 1,
        halfSize + 6 + i * 2, halfSize + 2
      );
    }

    graphics.generateTexture('enemy-ranged', size, size);
    graphics.destroy();
  }

  private createDasherTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = GameConfig.ENEMY.SIZE;
    const halfSize = size / 2;

    // Shadow
    graphics.fillStyle(0x000000, 0.15);
    graphics.fillEllipse(halfSize, halfSize + 8, 10, 3);

    // Body (cyan/blue, agile looking)
    graphics.fillStyle(0x00CED1, 1);
    graphics.fillEllipse(halfSize, halfSize + 1, 8, 10);
    graphics.fillStyle(0x40E0D0, 1);
    graphics.fillEllipse(halfSize - 1, halfSize, 5, 7);

    // Head
    graphics.fillStyle(0x00CED1, 1);
    graphics.fillCircle(halfSize, halfSize - 3, 5.5);
    graphics.fillStyle(0x40E0D0, 1);
    graphics.fillCircle(halfSize - 1, halfSize - 4, 3);

    // Ears
    graphics.fillStyle(0x008B8B, 1);
    graphics.fillCircle(halfSize - 4, halfSize - 6, 2.5);
    graphics.fillCircle(halfSize + 4, halfSize - 6, 2.5);

    // Eyes (cyan glow)
    graphics.fillStyle(0x00FFFF, 1);
    graphics.fillCircle(halfSize - 2, halfSize - 4, 1);
    graphics.fillCircle(halfSize + 2, halfSize - 4, 1);
    graphics.fillStyle(0x7FFFD4, 0.5);
    graphics.fillCircle(halfSize - 2, halfSize - 4, 2);
    graphics.fillCircle(halfSize + 2, halfSize - 4, 2);

    // Lightning bolt effect
    graphics.lineStyle(1.5, 0xFFFF00, 0.6);
    graphics.lineBetween(halfSize - 6, halfSize + 2, halfSize - 4, halfSize);
    graphics.lineBetween(halfSize - 4, halfSize, halfSize - 6, halfSize - 2);

    graphics.generateTexture('enemy-dasher', size, size);
    graphics.destroy();
  }

  update(): void {
    if (!this.target || !this.active) return;

    // Special behaviors for different enemy types
    if (this.enemyType === 'ranged') {
      this.updateRangedBehavior();
      return;
    }

    if (this.enemyType === 'dasher') {
      this.updateDasherBehavior();
      return;
    }

    // Default: Move towards player
    const direction = new Phaser.Math.Vector2(
      this.target.x - this.x,
      this.target.y - this.y
    );

    direction.normalize();
    direction.scale(this.speed);

    this.setVelocity(direction.x, direction.y);

    // Remove if too far from world
    const worldBounds = this.scene.cameras.main.worldView;
    const margin = 200;
    if (
      this.x < worldBounds.x - margin ||
      this.x > worldBounds.right + margin ||
      this.y < worldBounds.y - margin ||
      this.y > worldBounds.bottom + margin
    ) {
      this.destroy();
    }
  }

  private updateRangedBehavior(): void {
    // Keep distance from player and shoot
    const distance = Phaser.Math.Distance.Between(
      this.x, this.y,
      this.target.x, this.target.y
    );

    const optimalDistance = 150;

    if (distance < optimalDistance) {
      // Move away from player
      const direction = new Phaser.Math.Vector2(
        this.x - this.target.x,
        this.y - this.target.y
      );
      direction.normalize();
      direction.scale(this.speed);
      this.setVelocity(direction.x, direction.y);
    } else {
      // Stop moving
      this.setVelocity(0, 0);
    }

    // Shoot at player periodically
    if (!this.shootTimer) {
      this.shootTimer = this.scene.time.addEvent({
        delay: 2000,
        callback: () => {
          this.shootAtPlayer();
        },
        callbackScope: this,
        loop: true
      });
    }
  }

  private shootAtPlayer(): void {
    if (!this.active || !this.target) return;

    // Create a simple projectile
    const angle = Phaser.Math.Angle.Between(
      this.x, this.y,
      this.target.x, this.target.y
    );

    const projectile = this.scene.add.circle(this.x, this.y, 3, 0xFF4500);
    this.scene.physics.add.existing(projectile);
    const body = projectile.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(
      Math.cos(angle) * 200,
      Math.sin(angle) * 200
    );

    // Damage player on hit (handled by game scene collision)
    (projectile as any).enemyProjectile = true;
    (projectile as any).damage = 10;

    // Auto-destroy after 2 seconds
    this.scene.time.delayedCall(2000, () => {
      projectile.destroy();
    });
  }

  private updateDasherBehavior(): void {
    if (this.isDashing) {
      // Continue dashing in current direction
      return;
    }

    // Normal movement toward player
    const direction = new Phaser.Math.Vector2(
      this.target.x - this.x,
      this.target.y - this.y
    );

    direction.normalize();
    direction.scale(this.speed);
    this.setVelocity(direction.x, direction.y);

    // Setup dash timer
    if (!this.dashTimer) {
      this.dashTimer = this.scene.time.addEvent({
        delay: this.dashCooldown,
        callback: () => {
          this.performDash();
        },
        callbackScope: this,
        loop: true
      });
    }
  }

  private performDash(): void {
    if (!this.active || !this.target) return;

    this.isDashing = true;

    // Flash to indicate dash
    this.setTint(0x00FFFF);

    // Calculate dash direction
    const direction = new Phaser.Math.Vector2(
      this.target.x - this.x,
      this.target.y - this.y
    );

    direction.normalize();
    direction.scale(this.speed * 3); // 3x speed during dash

    this.setVelocity(direction.x, direction.y);

    // End dash after 500ms
    this.scene.time.delayedCall(500, () => {
      this.isDashing = false;
      this.clearTint();
    });
  }

  takeDamage(amount: number): void {
    this.health -= amount;

    // Visual feedback
    this.setTint(0xffffff);
    this.scene.time.delayedCall(50, () => {
      this.clearTint();
    });

    if (this.health <= 0) {
      this.die();
    }
  }

  private die(): void {
    // Special behavior for splitter: create 2 smaller enemies
    if (this.enemyType === 'splitter') {
      this.scene.events.emit('enemy-split', this.x, this.y, this.target);
    }

    // Create death particle effect
    this.createDeathEffect();

    // Emit death event with position for gem spawning
    this.scene.events.emit('enemy-killed', this.x, this.y);

    this.destroy();
  }

  private createDeathEffect(): void {
    // Get tint color based on type
    let tintColor = 0x808080;
    switch (this.enemyType) {
      case 'fast':
        tintColor = 0x8B4513;
        break;
      case 'tank':
        tintColor = 0x505050;
        break;
      case 'elite':
        tintColor = 0x9370DB;
        break;
      case 'splitter':
        tintColor = 0x32CD32;
        break;
      case 'ranged':
        tintColor = 0xFFA500;
        break;
      case 'dasher':
        tintColor = 0x40E0D0;
        break;
    }

    // Create small particles when enemy dies
    const particles = this.scene.add.particles(this.x, this.y, `enemy-${this.enemyType}`, {
      speed: { min: 50, max: 150 },
      scale: { start: 0.5, end: 0 },
      lifespan: 300,
      quantity: 8,
      angle: { min: 0, max: 360 },
      tint: tintColor
    });

    // Auto-destroy particle emitter
    this.scene.time.delayedCall(400, () => {
      particles.destroy();
    });
  }

  destroy(): void {
    // Clean up timers
    if (this.dashTimer) {
      this.dashTimer.destroy();
    }
    if (this.shootTimer) {
      this.shootTimer.destroy();
    }
    super.destroy();
  }
}
